import type { MonitorArrival } from '@/apis/monitor';
import {
  describeArrivalStatus,
  formatBoardTime,
  formatDestination,
  landingTime,
  pickBoardRows,
} from './arrivalRows';

interface ArrivalsBoardProps {
  /** null until the first response arrives. */
  arrivals: MonitorArrival[] | null;
  windowHours: number;
}

/** Airport-style arrivals board shown under the map on the overview step. */
const ArrivalsBoard = ({ arrivals, windowHours }: ArrivalsBoardProps) => {
  if (arrivals === null) return null;
  const rows = pickBoardRows(arrivals);

  return (
    <section className='monitor-arrivals' aria-label='Arrivals'>
      <div className='monitor-arrivals-header'>
        <span className='monitor-arrivals-title'>Arrivals</span>
        <span className='monitor-arrivals-subtitle'>
          Last hour and next {windowHours} hours
        </span>
      </div>
      {rows.length === 0 ? (
        <p className='monitor-arrivals-empty'>
          No deliveries due in the next {windowHours} hours.
        </p>
      ) : (
        <table className='monitor-arrivals-table'>
          <thead>
            <tr>
              <th scope='col'>Scheduled</th>
              <th scope='col'>Reference</th>
              <th scope='col'>Destination</th>
              <th scope='col'>Courier</th>
              <th scope='col'>Zoomlogi ETA</th>
              <th scope='col'>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((arrival) => {
              const status = describeArrivalStatus(arrival);
              return (
                <tr
                  key={arrival.id}
                  className={
                    arrival.status === 'delivered'
                      ? 'monitor-arrivals-row--done'
                      : undefined
                  }
                >
                  <td className='monitor-arrivals-strong'>
                    {formatBoardTime(arrival.scheduled_delivery_date)}
                  </td>
                  <td
                    className='monitor-arrivals-strong monitor-arrivals-reference'
                    title={arrival.reference_number || undefined}
                  >
                    {arrival.reference_number || '—'}
                  </td>
                  <td
                    className='monitor-arrivals-destination'
                    title={formatDestination(arrival)}
                  >
                    {formatDestination(arrival)}
                  </td>
                  <td className='monitor-arrivals-courier'>
                    {arrival.courier_name || '—'}
                  </td>
                  <td className='monitor-arrivals-strong'>
                    {formatBoardTime(landingTime(arrival))}
                  </td>
                  <td>
                    <span
                      className={`monitor-arrivals-pill monitor-arrivals-pill--${status.tone}`}
                    >
                      {status.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default ArrivalsBoard;
